import React, { useCallback } from 'react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Icon, IIconStyles } from '@fluentui/react/lib/Icon';
import { Separator } from '@fluentui/react/lib/Separator';
import AdminHelpSupporter from './AdminHelpSupporter';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { createNewGuid } from '../../../../../components/Utils';
import { useSupporters } from '../useSupporters';
import { useSetQueryData } from '../../../../../hooks/useSetQueryData';
import { Supporter } from '../../../../../api/generated/data-contracts';
import { ApiCalls } from '../../../../../api/api';
import { useMutation } from '@tanstack/react-query';

function AdminSupporters() {
    const { data: supporters, queryKey } = useSupporters({ suspense: true });
    const setSupporters = useSetQueryData<typeof supporters>(queryKey);

    const ReorderSupporter = useCallback(
        (s: Supporter, direction: number) => {
            setSupporters(supporters => {
                let neworder: number = -1;

                if (direction > 0) {
                    neworder = s.order < supporters.length ? s.order + 1 : supporters.length;
                } else {
                    neworder = s.order > 1 ? s.order - 1 : 1;
                }

                let orderNumber: number = 1;

                return supporters
                    .map(supporter => {
                        if (neworder === orderNumber) {
                            orderNumber = orderNumber + 1;
                        }
                        if (supporter.id === s.id) {
                            // supporters[i].order = neworder;
                            return {
                                ...supporter,
                                order: neworder,
                            };
                        }
                        return {
                            ...supporter,
                            order: orderNumber++,
                        };
                    })
                    .sort((a, b) => {
                        return a.order < b.order ? -1 : 1;
                    });
            });
        },
        [setSupporters],
    );

    const RemoveSupporter = useCallback(
        (s: Supporter) => {
            setSupporters(supporters => supporters.filter(sup => sup.id !== s.id));
        },
        [setSupporters],
    );

    const SupporterUpdated = useCallback(
        (id: string, header: string, description: string, web: string, email: string, call: string) => {
            setSupporters(supporters =>
                supporters.map(supporter => {
                    if (supporter.id === id) {
                        return {
                            ...supporter,
                            header,
                            description,
                            web,
                            call,
                            email,
                        };
                    }
                    return supporter;
                }),
            );
        },
        [setSupporters],
    );

    const { mutate } = useMutation(() => ApiCalls.setSupporters(supporters));
    const saveChanges = useCallback(() => {
        mutate();
    }, [mutate]);

    const AddSupporterClick = useCallback(() => {
        setSupporters(supporters => [
            ...supporters,
            { id: createNewGuid(), header: 'New Supporter', description: '', web: '', email: '', call: '', order: supporters.length + 1 },
        ]);
    }, [setSupporters]);

    return (
        <div style={outerDivStyle}>
            <h3>Supporters</h3>
            <Separator>
                <Icon iconName="ProjectCollection" styles={separatorIconStyles} />
            </Separator>
            <br />
            <div style={{ display: 'inline-block' }}>
                <Stack tokens={{ childrenGap: 10 }}>
                    {supporters.map(supporter => {
                        return (
                            <AdminHelpSupporter
                                key={supporter.id}
                                ReorderSupporter={ReorderSupporter}
                                RemoveSupporter={RemoveSupporter}
                                SupporterUpdated={SupporterUpdated}
                                Supporter={supporter}
                            ></AdminHelpSupporter>
                        );
                    })}
                    <Separator>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <Stack.Item align="center">
                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    <DefaultButton text="Add Supporter" onClick={AddSupporterClick}></DefaultButton>
                                    <PrimaryButton onClick={saveChanges} text="Save Supporters" />
                                </Stack>
                            </Stack.Item>
                        </Stack>
                    </Separator>
                </Stack>
                <br />
                <br />
                <br />
            </div>
        </div>
    );
}

export default AdminSupporters;

const outerDivStyle = {
    margin: '20px 0px 0px 0px',
    display: 'block',
    justifyContent: 'center',
    textAlign: 'center',
} as React.CSSProperties;

const separatorIconStyles: IIconStyles = {
    root: {
        fontSize: '24px',
        height: '24px',
        width: '24px',
    },
};
